
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { PageOptions } from '../models/page-options.model';
import { Page } from '../models/page.model';
import { RemoteData } from '../models/remote-data.model';
import { ServerData } from '../models/server-data.model';
import { IApi } from './api.interface';

import { ErrorHandler } from '@angular/core';
import * as moment from 'moment';
import { Headers } from 'ng2-file-upload';
import { environment } from '../../../../environments/environment';
import { IAuth } from './auth.interface';
import { EncryptionDecryptionService } from '~/app/services/encryptDecrypt/encryption-decryption.service';

export class GenericApi<TModel> implements IApi<TModel> {

  private _createSubject = new Subject<TModel>();
  private _updateSubject = new Subject<TModel>();
  private _removeSubject = new Subject<string | number>();
  private _postSubject = new Subject<any>();
  private _bulkSubject = new Subject<any>();
  private _uploadSubject = new Subject<any>();

  private _auth: IAuth;
  private url: string;

  afterCreate = this._createSubject.asObservable();
  afterUpdate = this._updateSubject.asObservable();
  afterRemove = this._removeSubject.asObservable();
  afterPost = this._postSubject.asObservable();
  afterBulk = this._bulkSubject.asObservable();
  afterUpload = this._uploadSubject.asObservable();

  private encryptDecryptService: EncryptionDecryptionService;

  constructor(
    private http: HttpClient,
    private code: string,
    private options?: {
      auth?: IAuth,
      collection?: any,
      headers?: Array<{ key: string, value?: any }>,
      map?: (obj: any) => TModel
      extension?: string,
      errorHandler?: ErrorHandler,
      noRefresh?: boolean,
    }
  ) {
    this.options = this.options || {};
    this._auth = this.options.auth;
    this.encryptDecryptService = new EncryptionDecryptionService();
  }

  public get(id: number | string, options?: {
    watch?: number
    offline?: boolean,
    timeStamp?: Date,
    isDecrypt?: boolean,
    map?: (obj: any) => TModel
  }): Observable<TModel> {
    if (!this.options.noRefresh) {
      this._auth.toogleIdle()
    }
    options = options || {};
    const subject = new Subject<TModel>();

    let timeStamp: Date = options.timeStamp;
    const ticker = (options.watch ? timer(0, options.watch) : timer(0)).subscribe(() => {
      const url = this.apiUrl(id);
      const requestTime = new Date();
      const request = this.http
        .get<ServerData<TModel>>(url, { headers: this.getHeaders(timeStamp) })
        .subscribe(
          async (dataModel: any) => {
            try {
              const isSuccess = dataModel.isSuccess !== undefined ? dataModel.isSuccess : (dataModel as any).IsSuccess;
              if (!isSuccess) {
                const error = new Error(dataModel.error || dataModel.code || dataModel.message || 'failed');
                return this.handleError(error, subject, request);
              }

              let model;
              if (options.isDecrypt) {
                try {
                  if (dataModel.items) {
                    dataModel.message = dataModel.items.toString();
                  }
                  await this.encryptDecryptService.decrypt(dataModel.message).then((res) => {
                    dataModel.data = JSON.parse(res);
                  });
                } catch (error) {
                  console.error('Error decrypting items:', error);
                  throw new Error('Failed to decrypt response items');
                }
              }

              if (options && options.map) {
                model = options.map(dataModel.data);
              } else if (this.options.map) {
                model = this.options.map(dataModel.data);
              } else {
                model = dataModel.data;
              }

              if (!model && timeStamp) {
                return;
              }

              if (model && (model as any)['timeStamp']) {
                const modelTimeStamp = (model as any)['timeStamp'];

                if (!timeStamp || !moment(timeStamp).isSame(modelTimeStamp, 'millisecond')) {
                  timeStamp = moment(modelTimeStamp).toDate();
                  return subject.next(model);
                } else {
                  return;
                }
              }

              timeStamp = requestTime;
              subject.next(model);
            } catch (err) {
              this.handleError(err, subject);
            }
          },
          (err) => {
            if (err.status === 304) { // Not Modified
              // its ok - we need not let user know anything
              return;
            }
            this.handleError(err, subject);
          });

      if (!subject.observers || !subject.observers.length) {
        ticker.unsubscribe();
        request.unsubscribe();
        return;
      }
    });
    return subject.asObservable();
  }
  public search(query?: any, options?: {
    offset?: number,
    limit?: number,
    offline?: boolean,
    sort?: string,
    desc?: boolean,
    path?: string
    map?: (obj: any) => TModel,
    isDecrypt?: boolean
  } | PageOptions): Observable<Page<TModel>> {
    if (!this.options.noRefresh) {
      this._auth.toogleIdle()
    }
    options = options || {};
    const headers = this.getHeaders();
    const pageOptions = new PageOptions({
      offset: options.offset,
      limit: options.limit,
      sort: options.sort,
      desc: options.desc,
      path: options.path
    });

    const mapper = options instanceof PageOptions ? null : options.map;
    const subject = new Subject<Page<TModel>>();
    const request = this.http
      .get<Page<TModel>>(this.getSearchUrl(query, pageOptions), { headers })
      .subscribe(
        async (response) => {

          if (options.isDecrypt && response.isSuccess) {
            try {
              const items = response.items.toString();
              const decryptedItems = await this.encryptDecryptService.decrypt(items);
              response.items = JSON.parse(decryptedItems);
            } catch (error) {
              console.error('Error decrypting items:', error);
              throw new Error('Failed to decrypt response items');
            }
          }
          this.extractPage(response, { map: mapper }, subject, request)
        },
        (err) => this.handleError(err, subject, request));
    return subject.asObservable();
  }

  public create(model: any, options?: {
    offline?: boolean,
    isDecrypt?: boolean,
    map?: (obj: any) => TModel
  }): Observable<TModel> {
    if (!this.options.noRefresh) {
      this._auth.toogleIdle()
    }

    options = options || {};
    const subject = new Subject<TModel>();

    const request = this.http
      .post<ServerData<TModel>>(this.apiUrl(), JSON.stringify(model), { headers: this.getHeaders() })
      .subscribe(
        async (response) => {
          if (options.isDecrypt && response.isSuccess) {
            try {
            await this.encryptDecryptService.decrypt(response.message).then((res) => {
              response.data = JSON.parse(res);
            });
            } catch (error) {
              console.error('Error decrypting items:', error);
              throw new Error('Failed to decrypt response items');
            }
          }
          const item = this.extractModel(response, options, subject, request);
          this._createSubject.next(item);
        },
        (err) => this.handleError(err, subject, request));
    return subject.asObservable();
  }
  public update(id: number | string, model: any, options?: {
    offline?: boolean,
    isDecrypt?: boolean,
    map?: (obj: any) => TModel
  }): Observable<TModel> {
    if (!this.options.noRefresh) {
      this._auth.toogleIdle()
    }
    options = options || {};
    const subject = new Subject<TModel>();

    const request = this.http
      .put<ServerData<TModel>>(this.apiUrl(id), JSON.stringify(model), { headers: this.getHeaders() })
      .subscribe(
        async (response) => {
          try {
            if (options.isDecrypt && response.isSuccess) {
              await this.encryptDecryptService.decrypt(response.message).then((res) => {
                response.data = JSON.parse(res);
              });
            }
          } catch (error) {
            console.error('Error decrypting items:', error);
            throw new Error('Failed to decrypt response items');
          }
          const item = this.extractModel(response, options, subject, request);
          this._updateSubject.next(item);
        },
        (err) => this.handleError(err, subject, request));
    return subject.asObservable();
  }
  public remove(id: number | string, options?: {
    offline?: boolean
  }): Observable<void> {
    if (!this.options.noRefresh) {
      this._auth.toogleIdle()
    }
    options = options || {};
    const subject = new Subject<void>();

    const request = this.http
      .delete<RemoteData>(this.apiUrl(id), { headers: this.getHeaders() })
      .subscribe(
        (dataModel) => {
          const isSuccess = dataModel.isSuccess !== undefined ? dataModel.isSuccess : (dataModel as any).IsSuccess;

          if (isSuccess) {
            this._removeSubject.next(id);
          }
          if (!subject.observers || !subject.observers.length) {
            request.unsubscribe();
            return;
          }
          if (isSuccess) {
            return subject.next();
          }

          const err = new Error(dataModel.error || dataModel.code || dataModel.message || 'failed');
          this.handleError(err, subject);
        },
        (err) => this.handleError(err, subject, request));
    return subject.asObservable();
  }

  public post(data: any, field: string, options?: {
    isDecrypt?: boolean,
    map?: (obj: any) => any
  }): Observable<any> {
    if (!this.options.noRefresh) {
      this._auth.toogleIdle()
    }

    options = options || {};

    const subject = new Subject<any>();

    const request = this.http
      .post<ServerData<any>>(this.apiUrl(field), JSON.stringify(data), { headers: this.getHeaders() })
      .subscribe(
        async (dataModel) => {
          const shouldHandle = this.shouldHandle(subject, request);

          const isSuccess = dataModel.isSuccess !== undefined ? dataModel.isSuccess : (dataModel as any).IsSuccess;

          if (!isSuccess) {
            if (shouldHandle) {
              const error = new Error(dataModel.error || dataModel.code || dataModel.message || 'failed');
              this.handleError(error, subject);
            }
            return;
          }

          if (options.isDecrypt) {
            try {
              await this.encryptDecryptService.decrypt(dataModel.message).then((res) => {
                dataModel.data = JSON.parse(res);
              });
            } catch (error) {
              console.error('Error decrypting items:', error);
              throw new Error('Failed to decrypt response items');
            }
          }

          const item = options.map ? options.map(dataModel.data) : dataModel.data;

          this._postSubject.next(item);
          if (shouldHandle) {
            subject.next(item);
          }
        },
        (err) => this.handleError(err, subject, request));
    return subject.asObservable();
  }

  public bulk(models: TModel[], path?: string, options?: {
    map?: (obj: any) => TModel
  }): Observable<any> {
    if (!this.options.noRefresh) {
      this._auth.toogleIdle()
    }
    options = options || {};
    const subject = new Subject<any>();

    const request = this.http
      .post<Page<TModel>>(this.apiUrl(path || 'bulk'), JSON.stringify({ items: models }), { headers: this.getHeaders() })
      .subscribe(
        (response) => {
          if (!response.isSuccess) {
            this.handleError(response.message, subject, request);
          } else {
            subject.next(response.message);
          }
        },
        (err) => this.handleError(err, subject, request));
    return subject.asObservable();
  }

  public upload(file: File, path?: string, query?: any, options?: any): Observable<any> {
    if (!this.options.noRefresh) {
      this._auth.toogleIdle()
    }
    const params = new URLSearchParams();
    for (const key in query) {
      if (query[key]) {
        params.set(key, query[key]);
      }
    }
    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl(path)}?${queryString}` : this.apiUrl(path);

    const headers: Headers[] = [];

    const httpHeaders = this.getHeaders();
    for (const name of httpHeaders.keys()) {
      const value = httpHeaders.get(name);

      if (name === 'Content-Type' || !value) {
        continue;
      }

      headers.push({
        name,
        value
      });
    }

    const uploader = new FileUploader({
      url,
      headers,
      autoUpload: true
    });

    uploader.onBeforeUploadItem = (item) => {
      item.withCredentials = false;
    };

    const subject = new Subject<any>();

    uploader.onErrorItem = (item: FileItem, response: string, status: number) => {
      const error = new Error('failed');
      this.handleError(error, subject);
    };

    uploader.onCompleteItem = async (item: FileItem, response: string, status: number, options?: any) => {
      const dataModel = JSON.parse(response) as ServerData<any>;
      const isSuccess = dataModel.isSuccess !== undefined ? dataModel.isSuccess : (dataModel as any).IsSuccess;

      if (!isSuccess) {
        const error = new Error(dataModel.error || dataModel.code || dataModel.message || 'failed');
        this.handleError(error, subject);
      } else {
        if(options.isDecrypt){
          // let data = await this.encryptDecryptService.decrypt(dataModel.message)
          await this.encryptDecryptService.decrypt(dataModel.message).then((res) => {
            subject.next(JSON.parse(res))
            this._uploadSubject.next(JSON.parse(res))
          });
        }else{
          subject.next(dataModel.data)
          this._uploadSubject.next(dataModel.data)
        }
      }
    };

    uploader.addToQueue([file]);

    return subject.asObservable();
  }

  private getHeaders(timeStamp?: Date): HttpHeaders {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (timeStamp) {
      headers['If-Modified-Since'] = timeStamp.toISOString();
    }
    if (this.options.headers && this.options.headers.length > 0) {
      this.options.headers.forEach((item) => {
        let value: string | null;
        if (item.value) {
          switch (typeof item.value) {
            case 'string':
              value = item.value;
              break;
            case 'function':
              value = item.value();
              break;

            default:
              value = JSON.stringify(item.value);
              break;
          }
        } else {
          value = localStorage.getItem(item.key);
        }

        if (value) {
          headers[item.key] = value;
        }
      });
    }

    let orgCode = '';
    let tenantCode = '';

    if (this._auth) {
      const role = this._auth.currentRole();
      const organization = this._auth.currentOrganization();
      const tenant = this._auth.currentTenant();
      const session = this._auth.currentSession();
      if (session && session.id) {
        headers['x-session-id'] = session.id;
      }

      if (role && role.token) {
        headers['authorization'] = role.token;
      }

      if (organization && organization.code) {
        orgCode = organization.code;
      }

      if (tenant && tenant.code) {
        tenantCode = tenant.code;
      }
    }

    if (environment.organization && environment.organization.code) {
      orgCode = environment.organization.code;
    }

    if (environment.code) {
      tenantCode = environment.code;
    }

    if (orgCode) {
      headers['x-organization-code'] = orgCode;
    }

    if (tenantCode) {
      headers['x-tenant-code'] = tenantCode;
    }

    return new HttpHeaders(headers);
  }

  private apiUrl(field?: number | string): string {

    if (!this.url) {

      if (this.code.toLowerCase().startsWith('http://') ||
        this.code.toLowerCase().startsWith('https://')
      ) {
        this.url = this.code;
      } else if (this._auth) {
        const tenant = this._auth.currentTenant();

        if (tenant && tenant.services) {
          const service = tenant.services.find((s) => s.code === this.code);
          if (service) {
            this.url = service.url;
          }
        }
      }
    }

    if (!this.url) {
      throw new Error(`${this.code} is invalid`);
    }

    let url = this.url;

    if (this.options.collection) {

      let key: string;

      switch (typeof this.options.collection) {
        case 'string':
          key = this.options.collection;
          break;
        case 'function':
          key = this.options.collection();
          break;
        default:
          key = JSON.stringify(this.options.collection);
          break;
      }

      url = `${url}/${key}`;
    }

    if (field) {
      url = `${url}/${field}`;
    }

    if (this.options.extension) {
      url = `${url}.${this.options.extension}`;
    }

    return url;
  }

  private getSearchUrl(query: any, options?: PageOptions): string {

    const params = new URLSearchParams();
    // tslint:disable-next-line:prefer-const
    for (let key in query) {
      if (query[key] !== undefined) {
        params.set(key, query[key]);
      }
    }

    if (options) {
      if (options.offset || options.limit) {
        options.offset = options.offset || 0;
        options.limit = options.limit || 10;
        params.set('offset', options.offset.toString());
        params.set('limit', options.limit.toString());
      }

      if (options.sort) {
        params.set('sort', options.sort.toString());
      }

      if (options.desc !== undefined) {
        params.set('desc', options.desc.toString());
      }
    }

    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl()}${options.path ? '/' + options.path : ''}?${queryString}` : this.apiUrl();

    return url;
  }

  private extractModel(
    dataModel: ServerData<TModel>,
    options: { map?: (obj: any) => TModel },
    subject: Subject<TModel>,
    request: Subscription): TModel {
    options = options || {};

    const shouldHandle = this.shouldHandle(subject, request);

    const isSuccess = dataModel.isSuccess !== undefined ? dataModel.isSuccess : (dataModel as any).IsSuccess;
    if (!isSuccess) {
      const error = new Error(dataModel.error || dataModel.code || dataModel.message || 'failed');

      if (shouldHandle) {
        this.handleError(error, subject, request);
      }
      return null;
    }

    let model: TModel;
    if (options && options.map) {
      model = options.map(dataModel.data);
    } else if (this.options.map) {
      model = this.options.map(dataModel.data);
    } else {
      model = dataModel.data as TModel;
    }

    if (shouldHandle) {
      subject.next(model);
    }
    return model;
  }

  private extractPage(
    dataModel: Page<TModel>,
    options: { map?: (obj: any) => TModel },
    subject: Subject<Page<TModel>>,
    request?: Subscription): Page<TModel> {

    const shouldHandle = this.shouldHandle(subject, request);
    options = options || {};
    const isSuccess = dataModel.isSuccess !== undefined ? dataModel.isSuccess : (dataModel as any).IsSuccess;
    if (!isSuccess) {
      if (shouldHandle) {
        const error = new Error(dataModel.error || dataModel.code || dataModel.message || 'failed');
        this.handleError(error, subject);
      }

      return;
    }

    const data = (dataModel as any)['data'] || dataModel;
    const items: TModel[] = [];
    data.items.forEach((item: TModel) => {
      if (options.map) {
        items.push(options.map(item));
      } else if (this.options.map) {
        items.push(this.options.map(item));
      } else {
        items.push(item as TModel);
      }
    });

    let pageSize = data.pageSize;
    let total = data.total;
    if (items.length === 0) {
      pageSize = 0;
      total = 0;
    }

    const page: Page<TModel> = new Page<TModel>();
    page.pageNo = data.pageNo;
    page.pageSize = pageSize;
    page.total = total;
    page.stats = data.stats;
    page.items = items;

    if (shouldHandle) {
      subject.next(page);
    }
    return page;
  }

  private handleError(err: any, subject: Subject<any>, request?: Subscription) {
    if (!this.shouldHandle(subject, request)) {
      return;
    }

    let isLogout = false

    if (err.error) {
      if (err.error.error == "INVALID_DEVICE" || err.error.error == "SESSION_EXPIRED" || err.error.message == "INVALID_DEVICE" || err.error.message == "SESSION_EXPIRED" || err.error.code == "INVALID_DEVICE" || err.error.code == "SESSION_EXPIRED") {
        isLogout = true
      }
    }

    let error = err.error && (err.error.error || err.error.message || err.error.code) ? (err.error.error || err.error.message || err.error.code) : err.message || err;

    if (typeof error == "object") {

      if (error.error) {
        error = error.error
      }

      if (error.message) {
        error = error.message
      }

      if (error.code) {
        error = error.code
      }
    }
    //error = 'Oops! Something went wrong. Please try again.';
    if (this.options.errorHandler) {
      this.options.errorHandler.handleError(error);
    }
    subject.error(error);
    if (isLogout) {
      this.options.auth.logout()
    }
  }

  private shouldHandle(subject: Subject<any>, request?: Subscription): boolean {
    if (!subject) {
      return false;
    }
    if (!subject.observers || !subject.observers.length) {
      if (request) {
        request.unsubscribe();
      }
      return false;
    }

    return true;

  }

}
